import { useMemo, useState, type ReactNode } from "react";
import type { CvAnalysis } from "@shared/modules/x-extraction";
import { Badge } from "../../../core/components/ui/badge";
import { Icon } from "../../../core/components/ui/icon";
import { ANALYSIS_STATUS_LABELS, ANALYSIS_STATUS_VARIANT, formatMonths, fullName, keySkills, levelLabel } from "../lib/profile";

type SortKey = "nom" | "poste" | "experience" | "niveau";

const LEVEL_RANK = ["aucun", "primaire", "secondaire", "professionnel", "collegial", "universitaire_1", "universitaire_2", "universitaire_3"];

const SORTERS: Record<SortKey, (a: CvAnalysis, b: CvAnalysis) => number> = {
  nom: (a, b) => fullName(a).localeCompare(fullName(b), "fr"),
  poste: (a, b) => (a.profile?.title ?? "~").localeCompare(b.profile?.title ?? "~", "fr"),
  experience: (a, b) => (b.profile?.totals.experienceMonths ?? -1) - (a.profile?.totals.experienceMonths ?? -1),
  niveau: (a, b) => LEVEL_RANK.indexOf(b.profile?.totals.highestLevel ?? "") - LEVEL_RANK.indexOf(a.profile?.totals.highestLevel ?? ""),
};

/**
 * Tableau comparatif des CV analysés : une ligne par candidat, les colonnes
 * qui départagent (expérience, diplôme, langues, compétences). Le tri est
 * local quand `sortable` (rapport d'un lot), sinon l'ordre vient du serveur.
 */
export function AnalysisTable({
  items,
  onSelect,
  sortable = false,
  rowActions,
  empty = "Aucun CV.",
}: {
  items: CvAnalysis[];
  onSelect: (a: CvAnalysis) => void;
  sortable?: boolean;
  rowActions?: (a: CvAnalysis) => ReactNode;
  empty?: string;
}) {
  const [sort, setSort] = useState<SortKey | null>(null);
  const rows = useMemo(() => (sortable && sort ? [...items].sort(SORTERS[sort]) : items), [items, sort, sortable]);

  const header = (key: SortKey, label: string, className?: string) => (
    <th className={className} aria-sort={sort === key ? "descending" : undefined}>
      {sortable ? (
        <button type="button" className="analyses__sort" onClick={() => setSort(sort === key ? null : key)}>
          {label}
          {sort === key ? <Icon name="arrow-down" size={12} /> : null}
        </button>
      ) : (
        label
      )}
    </th>
  );

  return (
    <div className="analyses">
      <table className="table">
        <thead>
          <tr>
            {header("nom", "Candidat")}
            {header("poste", "Poste visé")}
            {header("experience", "Expérience")}
            {header("niveau", "Plus haut diplôme", "table__col--wide")}
            <th className="table__col--wide">Langues</th>
            <th className="table__col--wide">Compétences clés</th>
            <th>État</th>
            {rowActions ? <th /> : null}
          </tr>
        </thead>
        <tbody>
          {rows.map((a) => {
            const ready = a.status === "a_valider" || a.status === "applique";
            return (
              <tr key={a.jobId} className={ready ? "table__row--link" : undefined} onClick={ready ? () => onSelect(a) : undefined}>
                <td>
                  {ready ? (
                    <button
                      type="button"
                      className="analyses__name"
                      onClick={(e) => {
                        e.stopPropagation();
                        onSelect(a);
                      }}
                    >
                      {fullName(a)}
                    </button>
                  ) : (
                    <span>{fullName(a)}</span>
                  )}
                  {a.identity.email ? <span className="analyses__sub">{a.identity.email}</span> : null}
                </td>
                <td>{a.profile?.title ?? a.profile?.totals.lastTitle ?? <span className="muted">—</span>}</td>
                <td className="analyses__num">{formatMonths(a.profile?.totals.experienceMonths)}</td>
                <td className="table__col--wide">{levelLabel(a.profile?.totals.highestLevel)}</td>
                <td className="table__col--wide">{a.profile?.languages.map((l) => l.language).join(", ") || <span className="muted">—</span>}</td>
                <td className="table__col--wide">
                  <ul className="analyses__skills">
                    {keySkills(a, 4).map((s) => (
                      <li key={s} className="cv-tag">
                        {s}
                      </li>
                    ))}
                  </ul>
                </td>
                <td>
                  <Badge variant={ANALYSIS_STATUS_VARIANT[a.status]}>{ANALYSIS_STATUS_LABELS[a.status]}</Badge>
                  {a.error ? <span className="analyses__sub">{a.error}</span> : null}
                </td>
                {rowActions ? (
                  <td className="table__actions" onClick={(e) => e.stopPropagation()}>
                    {rowActions(a)}
                  </td>
                ) : null}
              </tr>
            );
          })}
          {rows.length === 0 ? (
            <tr>
              <td colSpan={rowActions ? 8 : 7} className="table__empty">
                {empty}
              </td>
            </tr>
          ) : null}
        </tbody>
      </table>
    </div>
  );
}
